import type { Context, Lifetime, Version } from '../../state/types/context.ts';
import { DomainKeys, type Peer, type State } from '../../state/types/state.ts';
import { check, parse } from '../../restrictions.ts';
import type { DomainUri } from '../../state/types/domain-registry.ts';
import type { HandleReturnType } from '../../domain.ts';
import type {
    ContextCreateRequest,
    ContextDelta,
    ParsedCreateRequest,
    RemoteContextCreateRequest
} from './types/requests.ts';
import { broadcast, error, isRemoteSource, success } from '../messages.ts';
import {
    byId,
    byIdInDomainThrows,
    byIdThrows,
    isLocalPeer,
    type JoinedPeer,
    leaveDomain,
    peersInDomain,
    visiblePeers
} from '../../state/peers.ts';
import {
    addContext,
    addContextMember,
    applyDelta,
    contextByIdThrows,
    contextByName,
    isClusterContext,
    newVersion,
    nextVersion,
    removeContext,
    removeContextMember,
    toCtx
} from './state.ts';
import type { Identity, PeerId, Restrictions, Source } from '../../state/types/common.ts';
import { contextAdded, contextCreated, contextDestroyed, contextUpdated, subscribedContext } from './messages.ts';
import { exceptionToReason, type Reason, throwReason } from '../../reason.ts';
import {
    contextBadLifetime,
    contextDestroyedExplicitly,
    contextDestroyedPeerLeft,
    contextNotAuthorized,
    failure
} from './constants.ts';
import { contextDomainUri, globalDomainUri } from '../../constants.ts';
import { contextId, nodeId } from '../ids.ts';
import { type Draft, produce } from 'immer';
import type { DomainMessage, MessageType, SendMessageAction } from '../../node.ts';
import { peerToAddress } from '../../address.ts';
import { getLogger } from '../../logger.ts';
import { contexts } from '../../domains/global/state.ts';
import { threadStateAndActions } from '../../state/core.ts';
import type {
    CreateContext,
    DestroyContext,
    SubscribeContext,
    UnsubscribeContext,
    UpdateContext
} from './types/messages.ts';
import { type PartialBy, some, type WithRequired } from '../utilities.ts'

/**
 * Checks if the request should be applied on an existing context
 */
function shouldUpdate(request: RemoteCreateContext | BroadcastUpdate, context: Context): boolean {
    const requestVersion = request.version;
    const contextVersion = context.version!;
    return (requestVersion.updates > contextVersion.updates)
        || ((requestVersion.updates === contextVersion.updates) && (requestVersion.timestamp >= contextVersion.timestamp));
}

// context permission
function canWrite(context: Context, peer: Peer, isExplicit = false): boolean {
    const lifetime = context.lifetime;

    if (peer.id === context.creator) return true;

    if (lifetime === 'activity') {
        //only members can write to an activity
        return context.members.has(peer.id);
    }
    // normal context - permissions apply
    if (peer.id === context.creator || peer.id === context.owner) {
        return true;
    }
    if (!isExplicit) {
        return check(context.permissions?.write, [undefined, context.identity], [peer.source, peer.identity]);
    } else {
        if (context.permissions?.write) {
            return check(context.permissions?.write, [undefined, context.identity], [peer.source, peer.identity]);
        }
        return false;
    }
}

/**
 * Checks whether a not a given peer can destroy a context
 */
function checkCanDestroy(domainUri: DomainUri, context: Context, peer: Peer) {
    if (context.lifetime === 'activity') {
        throwReason(contextNotAuthorized(domainUri), 'Activity contexts cannot be explicitly destroyed');
    }
    const owned = context.lifetime === 'ownership';
    if (!((owned && context.owner === peer.id) || (!owned && canWrite(context, peer)))) {
        throwReason(contextNotAuthorized(domainUri), 'Not authorized to destroy context');
    }
}

/**
 * Checks if a peer matches the restrictions of a context.
 */
function canRead(context: Context, peer: Peer): boolean {
    return peer.id === context.creator
        || peer.id === context.owner
        || check(context.permissions?.read, [undefined, context.identity], [peer.source, peer.identity])
        || canWrite(context, peer, true);
}

/**
 * Check if a context can be announced to a peer
 */
function canAnnounce(peer: Peer, context: Context): boolean {
    return isLocalPeer(peer) && context.lifetime !== 'activity' && canRead(context, peer);
}

/**
 * Checks if a peer matches the restrictions of a context.
 */
function checkCanRead(domainUri: DomainUri, context: Context, peer: Peer) {
    if (!canRead(context, peer)) {
        throwReason(contextNotAuthorized(domainUri), 'Not authorized to read context');
    }
}

function checkCanCreate(context: Context) {
    if (!context.local && !check(context.permissions?.write, [undefined, context.identity], [{type: 'cluster'}, undefined])) {
        throw new Error(`cannot create remote context '${context.name}' locally`);
    }
}

function remoteUnsubscribe(domainUri: DomainUri, state: State, source: Source, request: BroadcastUnsubscribe): HandleReturnType {
    const {peer_id: peerId, name} = request;
    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByName(state, name, peer);
        if (context) {
            return removePeerFromContext(domainUri, state, peerId, context);
        }
        else {
            if (logger.enabledFor('debug')) {
                logger.debug(`unable to find remote context ${name}`);
            }
            return [state, []];
        }
    }
    catch (e) {
        if (logger.enabledFor('debug')) {
            logger.debug(`error processing remote context unsubscribe ${JSON.stringify(request)}`, e);
        }
        return [state, []];
    }

}

function localUnsubscribe(domainUri: DomainUri, state: State, source: Source, request: UnsubscribeContext): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, context_id: contextId} = request;
    try {
        byIdThrows(state, peerId);
        const context = contextByIdThrows(domainUri, state, contextId);
        const [ns, rms] = removePeerFromContext(domainUri, state, peerId, context);
        let ms = rms.concat([
            success(domainUri, source, requestId, peerId)
        ]);
        if (isClusterContext(context)) {
            const message: BroadcastUnsubscribe = {
                ...request,
                type: 'unsubscribe-context',
                name: context.name
            };
            ms = ms.concat([broadcast(peerToAddress(nodeId(ns.ids), peerId), message)]);
        }
        return [ns, ms];
    } catch (e) {
        return [state, [error(domainUri, source, requestId, peerId, exceptionToReason(e, failure(domainUri)))]];
    }


}

/**
 * Unsubscribes a peer from context data change notifications
 */
export function forwardUnsubscribe(domainUri: DomainUri, state: State, source: Source, request: UnsubscribeContext) {
    return isRemoteSource(source) ? remoteUnsubscribe(domainUri, state, source, request as BroadcastUnsubscribe) : localUnsubscribe(domainUri, state, source, request);
}

/**
 * Checks which contexts the peer can see and sends announcements message correspondingly
 */
export function announceContexts(domainUri: DomainUri, state: State, peer: Peer): SendMessageAction<'context-added'>[] {
    const {source, id: peerId} = peer;
    return contexts(state)
        .filter(ctx => canAnnounce(peer, ctx))
        .map((ctx) => {
            const {creator: creatorId, id: ctxId, name: ctxName} = ctx;
            return contextAdded(domainUri, source, peerId, creatorId, ctxId, ctxName);
        });
}

export function peerToDomainUri(peer: Peer): DomainUri {
    if (peer.options?.["context-compatibility-mode?"]) {
        return globalDomainUri;
    } else {
        return contextDomainUri;
    }
}

// operations


/**
 * Updates a context. Returns a new state and announcement messages
 */
function update(state: State, context: Context, updaterId: PeerId, delta: ContextDelta, version: Version, _requestId): HandleReturnType {
    const contextId = context.id;
    const updatedState = applyDelta(state, context.id, delta, version);
    // todo log action

    return [updatedState,
        Array.from(context.members)
            .filter((member) => {
                return member !== updaterId
            })
            .map((peerId) => {
                return byId(updatedState, peerId);
            })
            .filter(some<Peer>)
            .filter((peer) => {
                return isLocalPeer(peer)
            })
            .map((peer) => {
                return contextUpdated(peerToDomainUri(peer), peer.source, peer.id, updaterId, contextId, delta)
            })];
}

/**
 * Processes a context creation request coming from a remote node
 */
function remoteUpdate(domainUri: DomainUri, state: State, request: BroadcastUpdate): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, name, delta, version} = request;
    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByName(state, name, peer);
        if (context) {
            if (canWrite(context, peer) && shouldUpdate(request, context)) {
                return update(state, context, peerId, delta, version, requestId);
            } else {
                return [state, []];
            }
        }
        else {
            if (logger.enabledFor('debug')) {
                logger.debug(`unable to find remote context ${name}`);
            }
            return [state, []];
        }
    }
    catch (e) {
        if (logger.enabledFor('debug')) {
            logger.debug(`error processing remote context update ${JSON.stringify(request)}`, e);
        }
        return [state, []];
    }
}

function localUpdate(domainUri: DomainUri, state: State, source: Source, request: UpdateContext): HandleReturnType {
    const {request_id:  requestId, peer_id: peerId, context_id: contextId, delta} = request;

    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByIdThrows(domainUri, state, contextId);
        const version: Version = nextVersion(context);
        if (!canWrite(context, peer)) {
            throwReason(contextNotAuthorized(domainUri), `Not authorized to update context`);
        }
        const message: BroadcastUpdate = {...request, type: 'update-context', version: version, name: context.name, delta};
        return threadStateAndActions(state,
            s => update(s, context, peerId, delta, version, requestId),
            s => [s, [success(domainUri, source, requestId, peerId)]],
            s => [s, isClusterContext(context) ? [broadcast(peerToAddress(nodeId(s.ids), peerId), message)] : []]);
    } catch (e) {
        return [state, [error(domainUri, source, requestId, peerId, exceptionToReason(e, failure(domainUri)))]];
    }

}

export function forwardUpdate(domainUri: DomainUri, state: State, source: Source, request: UpdateContext): HandleReturnType {
    return isRemoteSource(source) ? remoteUpdate(domainUri, state, request as BroadcastUpdate) : localUpdate(domainUri, state, source, request);
}


type RequestToContextOptions = {
    isLocal: boolean,
    defaultLifetime?: 'ref-counted' | 'ownership',
    defaultPermissions?: (type: 'read'|'write', name: string, creator: Identity) => Restrictions
};

type VersionedContext = WithRequired<Context, 'version'>;
/**
 * Builds the context state map
 */
function requestToContext(state: State, creator: Peer, request: ParsedCreateRequest, options: RequestToContextOptions): [State, VersionedContext] {
    const {name, data, peer_id: peerId} = request;

    // when not local always respect request lifetime, otherwise use override if not present or (retained and respect is not set)
    const requestedLifetime = request.lifetime ?? options.defaultLifetime;
    const overwriteRetained = options.isLocal && !(creator.options?.respect_context_lifetime ?? false);
    const lifetime: Lifetime = overwriteRetained && (requestedLifetime === 'retained' || requestedLifetime === undefined) ? options.defaultLifetime ?? 'retained' : requestedLifetime!;

    // set default read permissions
    const rp: Restrictions = request.permissions?.read ?? options?.defaultPermissions?.('read', name, creator.identity);
    const wp: Restrictions = request.permissions?.write ?? options?.defaultPermissions?.('write', name, creator.identity);
    const permissions = rp || wp ? {read: rp, write: wp} : undefined;

    const [newIds, ctxId] = contextId(state.ids);
    const version = request.version ?? newVersion();
    const ctx = produce(toCtx(creator, name, data, lifetime, request.read_permissions, request.write_permissions, permissions, ctxId, version), (draft: Draft<Context>) => {
        draft.members = new Set<PeerId>([peerId]);
        draft.local = options.isLocal;
        draft.lifetime =  lifetime;
        if (lifetime === 'ownership') {
            draft.owner = peerId;
        }
    });
    checkCanCreate(ctx);

    return [addContext({...state, ids: newIds}, ctx), ctx]
}

/**
 * Adds peer as a member of a context. Updates the state and generates messages.
 */
function subscribe(domainUri: DomainUri, state: State, sender: Source, requestId, context: Context, peerId: PeerId): HandleReturnType {
    // todo log action
    const updatedState = addContextMember(state, context, peerId);
    return [updatedState, [subscribedContext(domainUri, sender, requestId, peerId, context.id, context.data)]];
}

/**
 * Add the peer to the context members for the purpose of ref-counted context destruction
 */
function remoteSubscribe(domainUri: DomainUri, state: State, source: Source, request: RemoteSubscribeContext): HandleReturnType {
    const {peer_id: peerId, name} = request;
    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByName(state, name, peer);
        if (context) {
            checkCanRead(domainUri, context, peer);
            return [addContextMember(state, context, peerId), []];
        }
        else {
            if (logger.enabledFor('debug')) {
                logger.debug(`unable to find remote context ${name}`);
            }
            return [state, []];
        }

    }
    catch (e) {
        if (logger.enabledFor('debug')) {
            logger.debug(`error processing remote context subscribe ${JSON.stringify(request)}`, e);
        }
        return [state, []];
    }
}

/**
 * Processes a subscription request
 */
function localSubscribe(domainUri: DomainUri, state: State, source: Source, request: SubscribeContext): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, context_id: contextId} = request;
    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByIdThrows(domainUri, state, contextId);
        checkCanRead(domainUri, context, peer);
        const [ns, ms] = subscribe(domainUri, state, source, requestId, context, peerId);
        if (isClusterContext(context)) {
            const message: RemoteSubscribeContext = {
                ...request,
                type: 'subscribe-context',
                name: context.name
            };
            return [ns, ms.concat([broadcast(peerToAddress(nodeId(ns.ids), peerId), message)])];
        }
        return [ns, ms];
    } catch (e) {
        return [state, [error(domainUri, source, requestId, peerId, exceptionToReason(e, failure(domainUri)))]];
    }
}

/**
 * Subscribes a peer to context data changes
 */
export function forwardSubscribe(domainUri: DomainUri, state: State, source: Source, request): HandleReturnType {
    return isRemoteSource(source) ? remoteSubscribe(domainUri, state, source, request) : localSubscribe(domainUri, state, source, request);
}

//Broadcasts that the context is added to the local peers. The remote peers will be told so by their own node
function broadcastAdded(state: State, context: Context, creator: JoinedPeer<DomainKeys.Context>): SendMessageAction<'context-added'>[] {
    const ctxName = context.name;
    const ctxId = context.id;
    const creatorId = creator.id;
    return visiblePeers(state, DomainKeys.Context, creator, true)
        .filter((peer) => {
            return isLocalPeer(peer);
        })
        .filter((peer) => {
            return canRead(context, peer)
        })
        .map((peer) => {
            return contextAdded(peerToDomainUri(peer), peer.source, peer.id, creatorId, ctxId, ctxName)
        });
}

/**
 * Broadcasts that the context has been destroyed to the local peers. The remote peers will be told so by their own node
 */
function broadcastDestroyed(context: Context, peers: Peer[], reason: Reason): SendMessageAction<'context-destroyed'>[] {
    const ctxId = context.id;
    return peers
        .filter((peer) => isLocalPeer(peer))
        .filter((peer) => canRead(context, peer))
        .map((peer) => contextDestroyed(peerToDomainUri(peer), peer.source, peer.id, ctxId, reason));
}

function preprocess(domainUri: DomainUri, request: ContextCreateRequest): ParsedCreateRequest {
    const rp = parse(request.read_permissions);
    const wp = parse(request.write_permissions);
    const permissions = rp || wp ? {read: rp, write: wp} : undefined;
    const lifetime = request.lifetime; // todo no activity?
    if (!lifetime) {
        throwReason(contextBadLifetime(domainUri), `Bad lifetime value ${lifetime}`);
    }
    const result: ParsedCreateRequest = {...request , domain: domainUri, lifetime: lifetime, permissions};
    return result;
}

/**
 * Processes a context creation request coming from a remote node
 */
function remoteCreate(domainUri: DomainUri, state: State, request: RemoteCreateContext, options?: ContextOverrides): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, name} = request;
    try {
        const peer = byIdInDomainThrows(state, peerId, DomainKeys.Context);

        const existingContext = contextByName(state, name, peer);
        if (existingContext) {
            // the context already exists, we should either reset its image or ignore the request
            checkCanRead(domainUri, existingContext, peer)
            if (shouldUpdate(request, existingContext)) {
                return update(state, existingContext, peerId, {reset: request.data}, request.version, requestId);
            } else {
                return [state, []];
            }
        } else {
            // new context
            const processed = preprocess(domainUri, request);
            const [newState, ctx] = requestToContext(state, peer, processed, {isLocal:false, defaultPermissions: (_type, name, creator) => options?.defaultContextRestrictions?.apply(creator, [name])});
            return [newState, broadcastAdded(newState, ctx, peer)];
        }
    }
    catch (ex) {
        if (logger.enabledFor('debug')) {
            logger.debug(`error processing remote context create ${JSON.stringify(request)}`);
        }
        // swallow the exception since we cant send it back to the remote peer
        return [state, []];
    }
}

const logger = getLogger('gateway.contexts');

/**
 * Creates a context or subscribes for an already existing one
 */
function localCreate(domainUri: DomainUri, state: State, source: Source, request: ContextCreateRequest & Omit<DomainMessage, 'type'>, options?: ContextOverrides): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, name} = request;
    try {
        const peer = byIdInDomainThrows(state, peerId, DomainKeys.Context);
        const existingContext = contextByName(state, name, peer);
        if (existingContext) {
            // the context already exists

            checkCanRead(domainUri, existingContext, peer);
            return subscribe(domainUri, state, source, requestId, existingContext, peerId);
        } else {
            // new context

            const processed = preprocess(domainUri, request);
            const [newState, ctx] = requestToContext(
                state,
                peer,
                processed,
                {isLocal: true, defaultLifetime: options?.retainedOverride, defaultPermissions: (_type, name, creator) => options?.defaultContextRestrictions?.apply(creator, [name])}
            );
            // todo log action
            const messages: SendMessageAction<MessageType>[] = broadcastAdded(newState, ctx, peer);
            messages.push(contextCreated(domainUri, source, requestId, peerId, ctx.id));
            if (isClusterContext(ctx)) {
                const message: RemoteCreateContext = {
                    ...request,
                    type: 'create-context',
                    version: ctx.version,
                    lifetime: ctx.lifetime as 'ref-counted' | 'ownership' | 'retained'
                };
                messages.push(broadcast<'create-context'>(peerToAddress(nodeId(state.ids), peerId), message))
            }
            return [newState, messages];
        }
    } catch (e) {
        logger.error(`error creating context from request ${JSON.stringify(request)}`, e);
        return [state, [error(domainUri, source, requestId, peerId, exceptionToReason(e, failure(domainUri)))]];
    }
}

export type ContextOverrides = {
    retainedOverride?: 'ref-counted' | 'ownership'
    defaultContextRestrictions?: (this: Identity, name?: string) => Restrictions
}

export function forwardCreate(domainUri: DomainUri, state: State, source: Source, request: PartialBy<CreateContext | RemoteCreateContext, 'type' >, options?: ContextOverrides): HandleReturnType {
    return isRemoteSource(source) ? remoteCreate(domainUri, state, request as RemoteCreateContext, options) : localCreate(domainUri, state, source, request as CreateContext, options);
}

/**
 * Checks if a context is orphaned and can be destroyed
 */
function isOrphaned(context: Context): boolean {
    switch (context.lifetime) {
        case 'ownership': {
            return !context.owner;
        }
        case 'ref-counted': {
            return context.members.size === 0;
        }
        default: {
            return false;
        }
    }
}

/**
 * Destroys a context. Returns an updated state and vector of messages to send
 */
function destroy(domainUri: DomainUri, state: State, context: Context, reason: Reason): HandleReturnType {
    // todo log action
    const ctxId = context.id;
    const members = context.members;

    return [
        removeContext(state, ctxId),
        broadcastDestroyed(
            context,
            peersInDomain(state, DomainKeys.Context).filter(p => !members.has(p.id)),
            reason
        ).reduce(
            (a: SendMessageAction<MessageType>[], b: SendMessageAction<MessageType>) => {
                return a.concat(b);
            },
            Array.from(members)
                .map((peerId) => byId(state, peerId))
                .filter(some<Peer>)
                .filter(peer => isLocalPeer(peer))
                .map(peer => contextDestroyed(domainUri, peer.source, peer.id, ctxId, reason)))];

}

function remoteDestroy(domainUri: DomainUri, state: State, source: Source, request: BroadcastDestroy): HandleReturnType {
    const {peer_id: peerId, name} = request;
    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByName(state, name, peer);
        if (context) {
            checkCanDestroy(domainUri, context, peer);
            // destroy the context and send success
            return destroy(domainUri, state, context, contextDestroyedExplicitly(domainUri));
        }
        else {
            if (logger.enabledFor('debug')) {
                logger.debug(`unable to find remote context ${name}`);
            }
            return [state, []];
        }
    }
    catch (e) {
        if (logger.enabledFor('debug')) {
            logger.debug(`error processing remote destroy ${JSON.stringify(request)}`, e);
        }
        return [state, []];
    }


}

function localDestroy(domainUri: DomainUri, state: State, source: Source, request: DestroyContext): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, context_id: contextId} = request;
    try {
        const peer = byIdThrows(state, peerId);
        const context = contextByIdThrows(domainUri, state, contextId);
        checkCanDestroy(domainUri, context, peer);

        // destroy the context and send success

        const message: BroadcastDestroy = {
            ...request,
            type: 'destroy-context',
            name: context.name
        };
        return threadStateAndActions(state,
            s => destroy(domainUri, s, context, contextDestroyedExplicitly(domainUri)),
            s => [s, [success(domainUri, source, requestId, peerId)]],
            s => [s, isClusterContext(context) ? [broadcast(peerToAddress(nodeId(state.ids), peerId), message)] : []]);

    } catch (e) {
        return [state, [error(domainUri, source, requestId, peerId, exceptionToReason(e, failure(domainUri)))]];
    }
}

export type RemoteSubscribeContext = SubscribeContext & { name?: string };
export type RemoteCreateContext = CreateContext & RemoteContextCreateRequest;
export type BroadcastUnsubscribe = UnsubscribeContext & { name?: string };
export type BroadcastDestroy = DestroyContext & { name?: string };
export type BroadcastUpdate = UpdateContext & { name?: string, version: Version };

/**
 * Destroys a context
 */
export function forwardDestroy(domainUri: DomainUri, state: State, source: Source, request: DestroyContext | BroadcastDestroy): HandleReturnType {
    return isRemoteSource(source) ? remoteDestroy(domainUri, state, source, request as BroadcastDestroy) : localDestroy(domainUri, state, source, request);
}

/**
 * Handles an implicit context destruction caused by peer removal
 */
function removePeerFromContexts(domainUri: DomainUri, state: State, peer: Peer): HandleReturnType {
    const peerId = peer.id;
    return contexts(state).reduce(([s, ms]: HandleReturnType, c: Context) => {
        const [ns, nms] = removePeerFromContext(domainUri, s, peerId, c);
        return [ns, ms.concat(nms)] as HandleReturnType;
    }, [state, []] as HandleReturnType);
}

function removePeerFromContext(domainUri: DomainUri, state: State, peerId: PeerId, context: Context): HandleReturnType {
    if (context.members.has(peerId)) {
        const [newState, updatedCtx] = removeContextMember(state, context, peerId);
        if (isOrphaned(updatedCtx)) {
            return destroy(domainUri, newState, updatedCtx, contextDestroyedPeerLeft(domainUri));
        }
        return [newState, []];
    } else {
        return [state, []];
    }
}

/**
 * Peer is leaving the domain.
 *
 *   Updates the state and generates messages for all other peers and eventual destruction of contexts
 */
export function removePeer(domainUri: DomainUri, state: State, leavingPeer: JoinedPeer<DomainKeys.Context>, reason: Reason, isSourceRemoved: boolean): HandleReturnType {
    const [s, ms] = leaveDomain(domainUri, DomainKeys.Context, state, leavingPeer, reason, isSourceRemoved);

    const [ns, nms] = removePeerFromContexts(domainUri, s, leavingPeer);

    return [ns, nms.concat(ms.filter((m) => {
        return compatibilityModeFilter(ns, m);
    }))];
}

export function compatibilityModeFilter(state: State, msg: SendMessageAction<unknown>): boolean {
    const destinationPeer = byId(state, msg.body['peer_id']);
    if (destinationPeer) {
        return !destinationPeer.options?.["context-compatibility-mode?"];
    }
    return false;
}
